import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection';
import {AccountEntityTypeFilter} from 'src/components/filters';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {AccountSortField} from 'src/openapi/admin/models/AccountSortField';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';

import {SortHeaderFilter} from '@pb/kit-component-filters';

import {COLUMNS, FILTERING_WARNING, HEADERS, SELECTION_KEY} from '../../constants';
import {accountSearchResultStateActions, accountSearchResultStateSelector} from '../../slices/account-search-result-state.slice';
import {accountSearchResultResultSelector} from '../../slices/account-search-result.slice';

import type {SortDirection} from 'src/openapi/admin/models/SortDirection';

export const EntityTypeHeader = () => {
    const dispatch = useDispatch();
    const result = useSelector(accountSearchResultResultSelector);
    const cacheKeys = useSelector(accountSearchResultStateSelector);
    const {setErrorMessage} = useContext(MessageContext);

    const [entityType, setEntityType] = useState(
        cacheKeys.filters.entityType as string | undefined,
    );

    useEffect(() => {
        setEntityType(cacheKeys.filters.entityType as string | undefined);
    }, [cacheKeys.filters.entityType]);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(
            accountSearchResultStateActions.setSortCriteria({
                direction: direction as SortDirection,
                field: AccountSortField.ENTITY_TYPE,
            }),
        );
    }, [dispatch]);

    const applyFilter = useCallback(() => {
        const {entityType: _prevValue, ...otherFilters} = cacheKeys.filters;
        dispatch(clearSelection(SELECTION_KEY));

        if (entityType !== undefined) {
            dispatch(accountSearchResultStateActions.setFilters({
                ...otherFilters,
                entityType,
            }));
        } else {
            dispatch(accountSearchResultStateActions.setFilters(otherFilters));
        }
    }, [entityType, dispatch, cacheKeys.filters]);

    const clearFilter = useCallback(() => {
        const {entityType: _prevValue, ...otherFilters} = cacheKeys.filters;
        setEntityType(undefined);

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setFilters(otherFilters));
    }, [dispatch, cacheKeys.filters]);

    const cancelFilter = useCallback(() => {
        setEntityType(cacheKeys.filters.entityType as string | undefined);
    }, [cacheKeys.filters]);

    return (
        <SortHeaderFilter
            filterActive={!!cacheKeys.filters?.entityType}
            sortable={true}
            preventFilterOpening={(result && result.totalCount > MAX_SELECTED_ENTITIES)
                ? () => setErrorMessage(FILTERING_WARNING)
                : undefined}
            caption={HEADERS[COLUMNS.ENTITY_TYPE]}
            direction={cacheKeys.sortCriteria.field === AccountSortField.ENTITY_TYPE
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <AccountEntityTypeFilter selectedEntityType={entityType} onSelect={setEntityType}/>
        </SortHeaderFilter>
    );
};
